import {useEffect, useRef} from "react";
import {BUS_SOFT_API} from "../config";
import {AUTH} from "../auth/FirebaseContext";
import {dispatch, useSelector} from "../redux/store";
import {addCameraNotification, removeCameraNotificationFromBE, setCameraConfig} from "../redux/slices/camera";
import {updateTrafficLogFromEvent} from "../redux/slices/trafficLog";
import {getSessionOperator} from "../helper/session";
import {OPERATORS_GROUP} from "../constants";
import {getCameraConfigAPI} from "../api/camera";

let baseURL= BUS_SOFT_API

const CAMERA_ALLOWED_ROLES = [
    OPERATORS_GROUP.admin,
    OPERATORS_GROUP.superAdmin,
    OPERATORS_GROUP.operator,
    OPERATORS_GROUP.dispatcher,
];

export default function useCameraSocket() {
    const {notifications} = useSelector(state => state.camera)
    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(1000);

    useEffect(() => {
        let timeoutId;
        let cancelled = false;

        const init = async () => {
            const operator = getSessionOperator();
            if (!operator || !CAMERA_ALLOWED_ROLES.includes(operator.group)) {
                return;
            }

            try {
                const configRes = await getCameraConfigAPI();
                if (cancelled) return;
                if (!configRes.data || configRes.data.length === 0) {
                    return;
                }
                dispatch(setCameraConfig(configRes.data));
                setupEventSource();
            } catch (error) {
                console.error('Error fetching camera config:', error);
            }
        };

        const setupEventSource = async () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }

            try {
                const token = await AUTH.currentUser.getIdToken();
                const url = new URL(`${baseURL}camera/event-source?token=${token}`);
                eventSourceRef.current = new EventSource(url);

                eventSourceRef.current.onopen = () => {
                    reconnectTimeoutRef.current = 1000;
                };

                eventSourceRef.current.onmessage = (event) => {
                    try {
                        const newEvent = JSON.parse(event.data);
                        if(newEvent && newEvent.type === "INSERT") {
                            dispatch(addCameraNotification(newEvent));
                        }
                        if(newEvent && newEvent.type === "UPDATE") {
                            dispatch(removeCameraNotificationFromBE(newEvent.id));
                            if(newEvent.trafficLog){
                                dispatch(updateTrafficLogFromEvent(newEvent.trafficLog));
                            }
                        }
                    } catch (error) {
                        console.error("Error parsing SSE message:", error);
                    }
                };

                eventSourceRef.current.onerror = () => {
                    console.error("SSE connection error. Attempting to reconnect...");
                    eventSourceRef.current.close();
                    timeoutId = setTimeout(setupEventSource, reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = Math.min(reconnectTimeoutRef.current * 2, 30000);
                };
            } catch (error) {
                console.error('Error setting up SSE connection:', error);
                timeoutId = setTimeout(setupEventSource, reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = Math.min(reconnectTimeoutRef.current * 2, 30000);
            }
        };

        init();

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, []);

    return {
        notifications,
        socket: eventSourceRef?.current,
    };
}
